import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import axios from 'axios';
import { API } from '../lib/api';
import { useAuth } from './AuthContext';
import {
  jePodporovano, vytvorBalicekKlicu, odemkniSoukromyKlic, klicKonverzace,
  otiskHesla, novaSulProPrihlaseni,
} from '../lib/crypto';

/**
 * Klíče pro koncové šifrování za běhu aplikace.
 *
 * Odemčený soukromý klíč žije **jen v paměti téhle stránky**. Nikam se
 * neukládá – ani do sessionStorage, ani do localStorage. Když se stránka
 * obnoví, je pryč a musí se zadat heslo. To je schválně: klíč uložený
 * v prohlížeči by šel přečíst každému, kdo se k zařízení dostane.
 *
 * Stavy:
 *   nacita    ještě nevíme
 *   nepodporovano  prohlížeč neumí potřebné šifrování
 *   vypnuto   člen šifrování nezapnul
 *   zamceno   klíče má, ale v téhle záložce ještě nezadal heslo
 *   odemceno  hotovo, dá se šifrovat i číst
 */

const CryptoContext = createContext(null);

export function CryptoProvider({ children }) {
  const { user, token } = useAuth();
  const [stav, setStav] = useState('nacita');
  const [balicek, setBalicek] = useState(null);
  const soukromyKlic = useRef(null);
  // Klíče konverzací se počítají z ECDH, což chvíli trvá. Držíme si je
  // v paměti podle protějšku, ať se to nepočítá u každé zprávy znovu.
  const klicePodleUzivatele = useRef(new Map());

  const zamkni = useCallback(() => {
    soukromyKlic.current = null;
    klicePodleUzivatele.current.clear();
    setStav((s) => (s === 'odemceno' ? 'zamceno' : s));
  }, []);

  // Po odhlášení musí klíč z paměti zmizet okamžitě.
  useEffect(() => {
    if (!token) {
      soukromyKlic.current = null;
      klicePodleUzivatele.current.clear();
      setBalicek(null);
      setStav('nacita');
    }
  }, [token]);

  // Zjistíme, jestli člen klíče vůbec má.
  useEffect(() => {
    if (!token || !user) return;
    if (!jePodporovano()) { setStav('nepodporovano'); return; }

    let zivy = true;
    axios.get(`${API}/crypto/keys/me`)
      .then((r) => {
        if (!zivy) return;
        setBalicek(r.data.bundle);
        // Klíč v paměti může být z předchozího přihlášení v téže záložce.
        setStav(r.data.bundle ? (soukromyKlic.current ? 'odemceno' : 'zamceno') : 'vypnuto');
      })
      .catch(() => { if (zivy) setStav('vypnuto'); });
    return () => { zivy = false; };
  }, [token, user]);

  /**
   * Zapne šifrování: vyrobí klíče, uloží je a převede účet na přihlašování
   * otiskem hesla.
   *
   * Obojí patří k sobě: klíč ke zprávám se odemyká heslem, takže dokud server
   * heslo při přihlášení dostává, mohl by z něj klíč odvodit. Zapnutí šifrování
   * bez toho by byla polovičatá ochrana.
   */
  const zapni = useCallback(async (heslo) => {
    const novy = await vytvorBalicekKlicu(heslo);
    await axios.put(`${API}/crypto/keys/me`, novy);

    // Převod na otisk. Když se nepovede, klíče už uložené jsou a šifrování
    // funguje – jen server při přihlašování dál vidí heslo. Zkusí se příště.
    try {
      const novaSul = novaSulProPrihlaseni();
      await axios.post(`${API}/auth/upgrade-auth`, {
        auth_salt: novaSul,
        auth_proof: await otiskHesla(heslo, novaSul),
      });
    } catch { /* šifrování jede i tak */ }
    const klic = await odemkniSoukromyKlic(novy, heslo);
    if (!klic) return false;
    soukromyKlic.current = klic;
    klicePodleUzivatele.current.clear();
    setBalicek(novy);
    setStav('odemceno');
    return true;
  }, []);

  /** Odemkne klíč heslem. Vrací false, když heslo nesedí. */
  const odemkni = useCallback(async (heslo) => {
    let aktualni = balicek;
    if (!aktualni) {
      const r = await axios.get(`${API}/crypto/keys/me`).catch(() => null);
      aktualni = r?.data?.bundle;
      if (aktualni) setBalicek(aktualni);
    }
    if (!aktualni) return false;

    const klic = await odemkniSoukromyKlic(aktualni, heslo);
    if (!klic) return false;
    soukromyKlic.current = klic;
    klicePodleUzivatele.current.clear();
    setStav('odemceno');
    return true;
  }, [balicek]);

  /**
   * Klíč pro konverzaci s daným členem.
   *
   * Vrací `null`, když protějšek šifrování nemá – s takovým člověkem se psát
   * dá, jen ne šifrovaně, a rozhraní to musí říct.
   */
  const klicPro = useCallback(async (userId) => {
    if (!soukromyKlic.current || !userId) return null;
    const ulozeny = klicePodleUzivatele.current.get(userId);
    if (ulozeny) return ulozeny;

    try {
      const { data } = await axios.get(`${API}/crypto/public-key/${userId}`);
      const klic = await klicKonverzace(soukromyKlic.current, data.public_key);
      klicePodleUzivatele.current.set(userId, klic);
      return klic;
    } catch {
      return null;
    }
  }, []);

  const hodnota = useMemo(() => ({
    stav,
    zapnuto: stav === 'odemceno' || stav === 'zamceno',
    odemceno: stav === 'odemceno',
    zapni,
    odemkni,
    zamkni,
    klicPro,
  }), [stav, zapni, odemkni, zamkni, klicPro]);

  return <CryptoContext.Provider value={hodnota}>{children}</CryptoContext.Provider>;
}

export const useCrypto = () => {
  const c = useContext(CryptoContext);
  if (!c) throw new Error('useCrypto musí být uvnitř CryptoProvider');
  return c;
};
